/**
 * Package Detail/View Page
 * Display package information in read-only mode with edit/delete actions
 */

import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit, Trash2, DollarSign, Shield, CheckCircle, XCircle, Lock } from 'lucide-react';

import { usePackage, usePackageMutations } from '@/src/hooks/admin/usePackages';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useState } from 'react';
import { isProtectedPackage } from '@/src/constants/admin/packages';

export default function PackageDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { package: pkg, loading, error } = usePackage(id || '');
  const { deletePackage, loading: deleting } = usePackageMutations();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [forceDelete, setForceDelete] = useState(false);

  const handleDelete = async () => {
    if (!id) return;
    
    try {
      await deletePackage(id, forceDelete, pkg?.package_code);
      navigate('/admin/packages');
    } catch (err) {
      // Error handling in hook
      setDeleteDialogOpen(false);
    }
  };

  const handleEdit = () => {
    navigate(`/admin/packages/${id}/edit`);
  };

  if (loading) {
    return (
      <div className="container mx-auto py-8">
        <Card>
          <CardContent className="py-8">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              <span className="ml-2">กำลังโหลดข้อมูล...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !pkg) {
    return (
      <div className="container mx-auto py-8">
        <Card>
          <CardContent className="py-8">
            <div className="text-center">
              <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">ไม่พบข้อมูลแพ็กเกจ</h3>
              <p className="text-muted-foreground mb-4">
                {error || 'แพ็กเกจที่ต้องการดูไม่มีในระบบ'}
              </p>
              <Button onClick={() => navigate('/admin/packages')}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                กลับไปหน้ารายการ
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 max-w-5xl">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/packages')}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          กลับไปหน้ารายการ
        </Button>
        
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold">{pkg.name}</h1>
              <Badge variant={pkg.is_active ? 'default' : 'secondary'}>
                {pkg.is_active ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
              </Badge>
            </div>
            <p className="text-muted-foreground">
              รหัส: <code className="bg-muted px-2 py-1 rounded">{pkg.package_code}</code>
            </p>
            {pkg.description && (
              <p className="text-sm text-muted-foreground mt-2">{pkg.description}</p>
            )}
          </div>
          
          <div className="flex gap-2">
            <Button onClick={handleEdit} variant="default">
              <Edit className="mr-2 h-4 w-4" />
              แก้ไข
            </Button>
            <Button 
              onClick={() => {
                if (pkg && isProtectedPackage(pkg.package_code)) return;
                setDeleteDialogOpen(true);
              }}
              variant={pkg && isProtectedPackage(pkg.package_code) ? 'outline' : 'destructive'}
              disabled={deleting || (!!pkg && isProtectedPackage(pkg.package_code))}
              title={pkg && isProtectedPackage(pkg.package_code) ? 'ไม่สามารถลบ Protected Package ได้' : 'ลบแพ็กเกจ'}
            >
              {pkg && isProtectedPackage(pkg.package_code) ? (
                <>
                  <Lock className="mr-2 h-4 w-4" />
                  ผูกผัน
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  ลบ
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Basic Information */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            ข้อมูลพื้นฐาน
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">รหัสแพ็กเกจ</p>
              <p className="font-medium">{pkg.package_code}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">ชื่อแพ็กเกจ</p>
              <p className="font-medium">{pkg.name}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">สถานะ</p>
              <p className="font-medium">{pkg.is_active ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">ลำดับการแสดงผล</p>
              <p className="font-medium">{pkg.sort_order}</p>
            </div>
            {pkg.description && (
              <div className="col-span-2">
                <p className="text-sm text-muted-foreground">คำอธิบาย</p>
                <p className="font-medium">{pkg.description}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Pricing Information */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            ราคาและเครดิต
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">ราคารายเดือน (THB)</p>
              <p className="font-medium text-lg">฿{pkg.monthly_price.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">ราคารายเดือน (USD)</p>
              <p className="font-medium text-lg">${pkg.monthly_price_usd.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">เครดิตที่รวมในแพ็กเกจ</p>
              <p className="font-medium">
                {pkg.included_credits === -1 ? 'ไม่จำกัด' : pkg.included_credits.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">ระยะเวลาหมดอายุเครดิต</p>
              <p className="font-medium">{pkg.credit_expiry_period === 'never' ? 'ไม่หมดอายุ' : pkg.credit_expiry_period}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">อัตราเครดิตเพิ่ม (THB/เครดิต)</p>
              <p className="font-medium">฿{pkg.overage_rate}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">อัตราเครดิตเพิ่ม (USD/เครดิต)</p>
              <p className="font-medium">${pkg.overage_rate_usd}</p>
            </div>
            <div className="col-span-2">
              <p className="text-sm text-muted-foreground">จำนวนเครดิตซื้อเพิ่มสูงสุด</p>
              <p className="font-medium">{pkg.max_credit_purchase.toLocaleString()} เครดิต</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Features */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            คุณสมบัติ
          </CardTitle>
          <CardDescription>
            ฟีเจอร์และคุณสมบัติที่มีในแพ็กเกจนี้
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pkg.features.length > 0 ? (
            <ul className="space-y-2">
              {pkg.features.map((feature, index) => (
                <li key={index} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">ไม่มีคุณสมบัติที่กำหนด</p>
          )}
        </CardContent>
      </Card>

      {/* Permissions */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            สิทธิ์การใช้งาน
          </CardTitle>
          <CardDescription>
            API และสิทธิ์ที่ลูกค้าสามารถเข้าถึงได้
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pkg.permissions.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {pkg.permissions.map((permission, index) => (
                <Badge key={index} variant="outline">
                  {permission}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">ไม่มีสิทธิ์ที่กำหนด</p>
          )}
        </CardContent>
      </Card>
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>ยืนยันการลบแพ็กเกจ</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <div>คุณแน่ใจหรือไม่ที่จะลบแพ็กเกจ <strong>{pkg.name}</strong>?</div>
                {isProtectedPackage(pkg.package_code) ? (
                  <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                    <Lock className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      <strong>ไม่สามารถลบได้</strong> — แพ็กเกจนี้เป็น Protected Package ของระบบ
                      จึงไม่สามารถลบหรือปิดใช้งานได้
                    </span>
                  </div>
                ) : (
                  <div className="text-sm">
                    หากมีลูกค้าที่ใช้แพ็กเกจนี้อยู่ คุณต้องเลือก "ลบแบบบังคับ" เพื่อดำเนินการต่อ
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          {!isProtectedPackage(pkg.package_code) && (
            <div className="flex items-start space-x-3 rounded-md border p-4">
              <Checkbox
                id="force-delete"
                checked={forceDelete}
                onCheckedChange={(checked) => setForceDelete(checked === true)}
              />
              <div className="space-y-1 leading-none">
                <label
                  htmlFor="force-delete"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  ลบแบบบังคับ
                </label>
                <p className="text-sm text-muted-foreground">
                  ลบถึงแม้จะมีลูกค้าใช้งานแพ็กเกจนี้อยู่
                </p>
              </div>
            </div>
          )}
          
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>
              {isProtectedPackage(pkg.package_code) ? 'ปิด' : 'ยกเลิก'}
            </AlertDialogCancel>
            {!isProtectedPackage(pkg.package_code) && (
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 focus:ring-red-500 focus:ring-2 focus:ring-offset-2 font-semibold"
                autoFocus
              >
                {deleting ? 'กำลังลบ...' : '⚠️ เข้าใจแล้ว ดำเนินการต่อ'}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
